import {
	normalizePurchaseLines,
	type PurchaseLine,
} from "./hub-purchase-lines.js";
import { salesOrderIdOf } from "./hub-work-sales-order.js";

export type HubSalesOrderForInvoice = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	extra?: string;
	note: string;
	lines?: PurchaseLine[];
	invoiceId?: number | string;
	quotationId?: number | string;
};

export type HubInvoiceDoc = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	mark?: string;
	extra?: string;
	note: string;
	lines?: PurchaseLine[];
	salesOrderId?: number | string;
};

export function invoiceIdOf(value: number | string | undefined | null): number | null {
	if (value == null || value === "") return null;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

export function findInvoiceById(invoices: HubInvoiceDoc[], invoiceId: number): HubInvoiceDoc | undefined {
	return invoices.find((row) => row.id === invoiceId);
}

export function invoiceDetailPath(invoice: HubInvoiceDoc, tab = ""): string {
	const key = encodeURIComponent(invoice.ref_no?.trim() || String(invoice.id ?? ""));
	const query = tab ? `?tab=${encodeURIComponent(tab)}` : "";
	return `/hub/invoices/${key}${query}`;
}

export function salesOrderAcceptsInvoiceConversion(status: string): boolean {
	const normalized = status.trim().toLowerCase();
	return normalized === "confirmed" || normalized === "fulfilled";
}

function purchaseLineRecords(lines: PurchaseLine[] | undefined) {
	return normalizePurchaseLines(lines ?? []).map((line) => ({
		category: line.category ?? "other",
		description: line.description,
		amount: line.amount,
		qty: line.qty,
		unitPrice: line.unitPrice,
	}));
}

export function linkedInvoiceForSalesOrder(
	order: HubSalesOrderForInvoice,
	invoices: HubInvoiceDoc[],
): HubInvoiceDoc | undefined {
	const fromOrder = invoiceIdOf(order.invoiceId);
	if (fromOrder != null) {
		const byId = invoices.find((row) => row.id === fromOrder);
		if (byId) return byId;
	}
	if (order.id == null) return undefined;
	return invoices.find((row) => salesOrderIdOf(row.salesOrderId) === order.id);
}

export function linkedSalesOrderForInvoice(
	invoice: HubInvoiceDoc,
	salesOrders: HubSalesOrderForInvoice[],
): HubSalesOrderForInvoice | undefined {
	const soId = salesOrderIdOf(invoice.salesOrderId);
	if (soId == null) return undefined;
	return salesOrders.find((row) => row.id === soId);
}

export function salesOrderDeletionBlockedReason(
	order: HubSalesOrderForInvoice,
	invoices: HubInvoiceDoc[],
): string | null {
	if (order.id == null) return null;
	const invoice = linkedInvoiceForSalesOrder(order, invoices);
	if (!invoice) return null;
	const ref = invoice.ref_no?.trim() || (invoice.id != null ? `#${invoice.id}` : "invoice");
	return `This sales order is linked to invoice ${ref}. Delete that invoice first, then try again.`;
}

export function salesOrderInvoiceBlockedReason(
	order: HubSalesOrderForInvoice,
	invoices: HubInvoiceDoc[],
): string | null {
	if (order.id == null) return "Save this sales order before creating an invoice.";
	if (!salesOrderAcceptsInvoiceConversion(order.status)) return "Confirm this sales order before creating an invoice.";
	if (linkedInvoiceForSalesOrder(order, invoices)) return "This sales order already has a linked invoice.";
	const lines = normalizePurchaseLines(order.lines ?? []);
	if (lines.length === 0) return "Add at least one line item to the sales order.";
	return null;
}

export function buildInvoiceRecordFromSalesOrder(
	order: HubSalesOrderForInvoice,
	refNo: string,
	docAt: string,
): Record<string, unknown> {
	if (order.id == null) throw new Error("Sales order id required");
	const due = order.extra?.trim().slice(0, 10) ?? "";
	return {
		party: order.party,
		doc_at: docAt,
		extra: /^\d{4}-\d{2}-\d{2}$/.test(due) ? due : "",
		ref_no: refNo,
		status: "draft",
		mark: "commercial",
		note: order.note ?? "",
		lines: purchaseLineRecords(order.lines),
		salesOrderId: order.id,
	};
}

export function salesOrderRecordWithInvoiceLink(
	order: HubSalesOrderForInvoice,
	invoiceId: number | null,
): Record<string, unknown> {
	const record: Record<string, unknown> = { ...order };
	delete record.id;
	if (invoiceId == null) delete record.invoiceId;
	else record.invoiceId = invoiceId;
	return record;
}
